import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Lock, Trophy, ChevronLeft, ChevronRight, Crown } from "lucide-react";
import Navbar from "../components/Navbar";
import api from "../lib/api";
import { SECTORS, SECTOR_SIZE, getLevelName, isBossLevel } from "../lib/levels";

const PAGE_SIZE = 100;

export default function Sector() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const sector = useMemo(() => SECTORS.find((s) => s.id === id), [id]);
  const [progress, setProgress] = useState(null);
  const [page, setPage] = useState(0);
  // Highlighted level (driven by URL hash e.g. `/sector/beginner#500`).
  // The matching card scrolls into view and pulses briefly so the pilot
  // lands exactly on the level they were deep-linked to.
  const [highlightLevel, setHighlightLevel] = useState(null);
  // Refs keyed by global level number; set as cards render. We only need
  // the currently-highlighted card's ref so we don't bother with a full
  // map structure — a single ref + re-render does the job.
  const highlightedCardRef = useRef(null);
  // Map: level (e.g. 500) → first-blood killer first name. Only populated
  // for the 6 sector-boss levels that have been claimed.
  const [firstBloods, setFirstBloods] = useState({});

  useEffect(() => {
    api
      .get("/progress")
      .then((r) => {
        setProgress(r.data);
        if (sector) {
          // If the URL carries a `#<level>` hash and it falls inside
          // this sector, honour it — land the pilot on the page that
          // contains the deep-linked card. Otherwise default to the
          // page containing the first still-locked level (existing
          // behaviour).
          const hashLevel = parseInt((location.hash || "").replace("#", ""), 10);
          const inSector = Number.isFinite(hashLevel)
            && hashLevel >= sector.from
            && hashLevel <= sector.to;
          if (inSector) {
            const withinSector = hashLevel - sector.from; // 0..499
            const tp = Math.ceil(SECTOR_SIZE / PAGE_SIZE);
            setPage(Math.min(tp - 1, Math.floor(withinSector / PAGE_SIZE)));
            setHighlightLevel(hashLevel);
          } else {
            const unlocked = r.data.unlocked_level || 1;
            const withinSector = Math.min(SECTOR_SIZE, Math.max(0, unlocked - sector.from));
            const tp = Math.ceil(SECTOR_SIZE / PAGE_SIZE);
            setPage(Math.min(tp - 1, Math.floor(withinSector / PAGE_SIZE)));
          }
        }
      })
      .catch(() => setProgress({ unlocked_level: 1, high_scores: {}, total_score: 0 }));
    // First-blood map is small (≤ 6 entries) and shared across all sectors,
    // so we fetch it once on mount and look up by level number per card.
    api.get("/leaderboard/boss-first-bloods")
      .then((r) => {
        const map = {};
        for (const fb of r.data?.items || []) {
          map[fb.level] = fb.killer_name;
        }
        setFirstBloods(map);
      })
      .catch(() => {});
  }, [sector, location.hash]);

  // Once the highlighted card has rendered (after `page` settles), scroll
  // it into view and clear the pulse after ~2.5s. The ref is attached
  // only to the matching card below, so this fires at most once per hash.
  useEffect(() => {
    if (!highlightLevel) return undefined;
    const el = highlightedCardRef.current;
    if (!el) return undefined;
    // Delay a tick so the paginated grid has painted the target card.
    const scrollTimer = setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
    const clearTimer = setTimeout(() => setHighlightLevel(null), 2500);
    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(clearTimer);
    };
  }, [highlightLevel, page]);

  if (!sector) {
    return (
      <div className="relative min-h-screen">
        <div className="starfield" />
        <Navbar />
        <main className="relative z-10 flex items-center justify-center min-h-screen px-6">
          <div className="glass p-8 text-center">
            <div className="font-heading uppercase text-2xl text-[#ff007f] mb-2">
              Unknown sector
            </div>
            <Link to="/levels" className="btn-neon cyan">
              Back to sectors
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const unlocked = progress?.unlocked_level ?? 1;
  const hs = progress?.high_scores || {};
  // Map of `{ "500": 27000 }` — fastest clear time per boss in milliseconds.
  // Persisted server-side so the Speedrun PB survives across devices.
  const fastestBossKills = progress?.fastest_boss_kills || {};
  const bossRanks = progress?.boss_ranks || {};
  const pageStart = page * PAGE_SIZE;
  const pageEnd = Math.min(SECTOR_SIZE, pageStart + PAGE_SIZE);
  const totalPages = Math.ceil(SECTOR_SIZE / PAGE_SIZE);
  const doneInSector = Math.max(0, Math.min(SECTOR_SIZE, unlocked - sector.from));
  const pct = Math.round((doneInSector / SECTOR_SIZE) * 100);

  return (
    <div className="relative min-h-screen">
      <div className="starfield" />
      <Navbar />
      <main className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 fade-in">
        <Link
          to="/levels"
          className="inline-flex items-center gap-2 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/50 hover:text-[#00ffff] mb-4"
          data-testid="back-to-sectors"
        >
          <ArrowLeft className="w-3 h-3" /> All Sectors
        </Link>

        <header className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <div
              className="font-mono-cosmic text-[11px] tracking-[0.35em] uppercase mb-2"
              style={{ color: sector.accent }}
            >
              // Sector · {sector.name}
            </div>
            <h1 className="font-heading font-black uppercase text-4xl sm:text-5xl tracking-tighter">
              {sector.name} · {SECTOR_SIZE.toLocaleString()} Levels
            </h1>
            <p className="text-white/60 text-sm mt-1">
              Levels #{sector.from.toLocaleString()} – #{sector.to.toLocaleString()} · Reward · {sector.skinName}
            </p>
          </div>
          <div className="glass p-4 min-w-[220px]">
            <div className="font-mono-cosmic text-[10px] tracking-[0.3em] text-white/50 uppercase">
              Progress
            </div>
            <div
              className="font-heading font-black text-2xl mt-1"
              style={{ color: sector.accent }}
            >
              {doneInSector.toLocaleString()} / {SECTOR_SIZE.toLocaleString()}
              <span className="text-white/40 text-sm font-normal ml-2">{pct}%</span>
            </div>
            <div className="mt-2 h-1.5 bg-white/5 overflow-hidden">
              <div
                className="h-full transition-all"
                style={{
                  width: `${pct}%`,
                  background: `linear-gradient(90deg, ${sector.accent}, #ffffff22)`,
                }}
              />
            </div>
          </div>
        </header>

        {/* Page controls */}
        <div
          className="flex items-center justify-between mb-4"
          data-testid="sector-page-controls"
        >
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="glass px-3 py-2 border border-white/20 text-white/80 hover:border-[#00ffff] hover:text-[#00ffff] disabled:opacity-40 disabled:cursor-not-allowed font-mono-cosmic text-[10px] tracking-[0.25em] uppercase"
            data-testid="sector-prev-page"
          >
            <span className="inline-flex items-center gap-1">
              <ChevronLeft className="w-3 h-3" /> Prev
            </span>
          </button>
          <div className="font-mono-cosmic text-xs tracking-widest uppercase text-white/60">
            Levels #{(sector.from + pageStart).toLocaleString()} – #{(sector.from + pageEnd - 1).toLocaleString()} · Page {page + 1}/{totalPages}
          </div>
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="glass px-3 py-2 border border-white/20 text-white/80 hover:border-[#00ffff] hover:text-[#00ffff] disabled:opacity-40 disabled:cursor-not-allowed font-mono-cosmic text-[10px] tracking-[0.25em] uppercase"
            data-testid="sector-next-page"
          >
            <span className="inline-flex items-center gap-1">
              Next <ChevronRight className="w-3 h-3" />
            </span>
          </button>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2" data-testid="sector-level-grid">
          {Array.from({ length: pageEnd - pageStart }).map((_, i) => {
            const localIdx = pageStart + i; // 0..499
            const globalLevel = sector.from + localIdx;
            const locked = globalLevel > unlocked;
            const isCurrent = globalLevel === unlocked;
            const score = hs[String(globalLevel)] || 0;
            const isBoss = isBossLevel(globalLevel);
            // Space Worm levels — every 10-multiple level (excluding 500-
            // multiples which are already cinematic sector bosses). Small
            // worm at every 10, giant at every 20. Drives the worm icon +
            // worm-PB chip below so each every-10 card reads as its own
            // micro-speedrun.
            const isSpaceWormSmall = globalLevel > 0 && globalLevel % 10 === 0 && globalLevel % 20 !== 0 && globalLevel % 500 !== 0;
            const isSpaceWormGiant = globalLevel > 0 && globalLevel % 20 === 0 && globalLevel % 500 !== 0;
            const isSpaceWorm = isSpaceWormSmall || isSpaceWormGiant;
            // Skeleton Swarm — every 5th level that ISN'T already a
            // small/giant Leviathan or sector boss. The grid icon flags
            // pilots that the level holds a bone-white mini-boss trio
            // before they tap in.
            const isSkeletonSwarm = globalLevel > 0
              && globalLevel % 5 === 0
              && globalLevel % 10 !== 0
              && globalLevel % 500 !== 0;
            // Bosses you've personally defeated get the gold "Victor" treatment.
            const bossCleared = isBoss && score > 0;
            // The same `fastest_boss_kills` map drives both sector-boss PBs
            // (existing) and the new space-worm PBs. We accept either an
            // isBoss OR isSpaceWorm card as PB-eligible.
            const wormCleared = isSpaceWorm && score > 0;
            const pbMs = (isBoss || isSpaceWorm) ? fastestBossKills[String(globalLevel)] : null;
            const bossPbMs = isBoss ? pbMs : null;
            const bossPbLabel = bossPbMs
              ? bossPbMs < 60000
                ? `${(bossPbMs / 1000).toFixed(1)}s`
                : `${Math.floor(bossPbMs / 60000)}m${String(Math.floor((bossPbMs % 60000) / 1000)).padStart(2, "0")}s`
              : null;
            const wormPbLabel = wormCleared && pbMs
              ? pbMs < 60000
                ? `${(pbMs / 1000).toFixed(1)}s`
                : `${Math.floor(pbMs / 60000)}m${String(Math.floor((pbMs % 60000) / 1000)).padStart(2, "0")}s`
              : null;
            // Hidden bosses: a locked boss is a mystery — render as "???"
            // with a faint sealed-vault aesthetic instead of revealing the
            // boss number/name. Once the pilot reaches it (unlocked), the
            // boss card reveals normally. Keeps the milestone feeling fresh.
            const hiddenBoss = isBoss && locked;
            const isHighlighted = globalLevel === highlightLevel;
            const content = (
              <div
                ref={isHighlighted ? highlightedCardRef : undefined}
                className={`aspect-square relative border flex flex-col items-center justify-center transition p-1 ${
                  isHighlighted ? "ring-2 ring-[#00ffff] ring-offset-2 ring-offset-black animate-pulse" : ""
                } ${
                  hiddenBoss
                    ? "border-dashed border-[#ff007f]/35 bg-[#ff007f]/[0.04] text-[#ff007f]/60 cursor-not-allowed"
                    : locked
                    ? "border-white/5 bg-white/[0.02] text-white/30 cursor-not-allowed"
                    : bossCleared
                      ? "border-[#ffd700] bg-gradient-to-b from-[#ffd700]/15 to-[#ff007f]/15 text-white shadow-[0_0_18px_rgba(255,215,0,0.55)] hover:shadow-[0_0_24px_rgba(255,215,0,0.75)] ring-1 ring-[#ffd700]/40"
                    : isBoss
                      ? "border-[#ff007f] bg-[#ff007f]/10 text-white shadow-[0_0_14px_rgba(255,0,127,0.4)] hover:bg-[#ff007f]/15"
                    : isCurrent
                      ? "border-[#00ff41] bg-[#00ff41]/10 text-[#00ff41] shadow-[0_0_12px_rgba(0,255,65,0.25)]"
                      : score > 0
                        ? "border-white/20 bg-white/[0.04] text-white hover:border-[#ffd700] hover:shadow-[0_0_10px_rgba(255,215,0,0.2)]"
                        : "border-white/10 text-white/70 hover:border-white/40"
                }`}
                data-testid={hiddenBoss ? `level-btn-hidden-boss-${globalLevel}` : `level-btn-${globalLevel}`}
                title={hiddenBoss ? "??? · Sealed Boss · Reach this level to reveal" : `#${globalLevel} · ${isBoss ? "BOSS · " : ""}${getLevelName(globalLevel, sector)}${score ? ` · Best ${score.toLocaleString()}` : ""}${bossCleared ? " · DEFEATED" : ""}`}
              >
                {hiddenBoss ? (
                  <>
                    <Lock className="absolute top-1 right-1 w-2.5 h-2.5 text-[#ff007f]/60" />
                    <div className="font-heading font-black text-lg leading-none tracking-tighter"
                      style={{ textShadow: "0 0 6px rgba(255,0,127,0.5)" }}
                    >
                      ???
                    </div>
                    <div className="font-mono-cosmic text-[7px] tracking-[0.25em] uppercase text-[#ff007f]/70 leading-none mt-1">
                      Sealed
                    </div>
                  </>
                ) : (
                <>
                {locked && (
                  <Lock className="absolute top-1 right-1 w-2.5 h-2.5 text-white/40" />
                )}
                {/* Crown replaces the trophy for cleared bosses — bigger flex,
                    tinted gold against the gradient ring. */}
                {bossCleared ? (
                  <Crown
                    className="absolute top-1 left-1 w-3 h-3 text-[#ffd700]"
                    style={{ filter: "drop-shadow(0 0 4px rgba(255,215,0,0.8))" }}
                    data-testid={`level-boss-victor-${globalLevel}`}
                  />
                ) : score > 0 ? (
                  <Trophy className="absolute top-1 left-1 w-2.5 h-2.5 text-[#ffd700]" />
                ) : null}
                {isBoss && !locked && !bossCleared && (
                  <span className="absolute top-1 right-1 font-mono-cosmic text-[7px] tracking-[0.2em] text-[#ff007f] uppercase leading-none">
                    Boss
                  </span>
                )}
                {bossCleared && (
                  <span className="absolute top-1 right-1 font-mono-cosmic text-[7px] tracking-[0.2em] text-[#ffd700] uppercase leading-none"
                    style={{ textShadow: "0 0 4px rgba(255,215,0,0.6)" }}
                  >
                    Victor
                  </span>
                )}
                {/* Space-worm icon — sits at top-right ONLY on every-10/20
                    cards that aren't sector bosses. Click-through is
                    preserved (icon is `pointer-events-none`). The chrome-
                    blue palette mirrors the in-canvas worm so pilots can
                    instantly tell "this level has a Space Worm boss". */}
                {isSpaceWorm && !isBoss && !locked && (
                  <span
                    className="absolute top-0.5 right-0.5 leading-none pointer-events-none"
                    title={isSpaceWormGiant ? "Giant Space Leviathan encounter" : "Space Leviathan encounter"}
                    data-testid={`level-worm-icon-${globalLevel}`}
                    style={{
                      color: isSpaceWormGiant ? "#ff8eff" : "#c95fff",
                      filter: `drop-shadow(0 0 ${isSpaceWormGiant ? 5 : 3}px ${isSpaceWormGiant ? "rgba(255,142,255,0.85)" : "rgba(201,95,255,0.7)"})`,
                      fontSize: isSpaceWormGiant ? "11px" : "9px",
                    }}
                  >
                    ◉
                  </span>
                )}
                {/* Skeleton Swarm icon — small bone-white skull on every-5
                    cards (excluding 10/20/500). Tucked into the top-right
                    corner with the same `pointer-events-none` so taps still
                    fall through to the Link wrapper. */}
                {isSkeletonSwarm && !isBoss && !locked && (
                  <span
                    className="absolute top-0.5 right-0.5 leading-none pointer-events-none"
                    title="Skeleton Worm Swarm encounter"
                    data-testid={`level-skeleton-icon-${globalLevel}`}
                    style={{
                      color: "#dde3eb",
                      filter: "drop-shadow(0 0 3px rgba(174,240,255,0.7))",
                      fontSize: "10px",
                    }}
                  >
                    ☠
                  </span>
                )}
                {/* First-blood holder ribbon — gold, ALL UPPERCASE, bottom
                    edge of the card. Truncates the killer name to fit. Only
                    renders on boss cards that have a recorded first-blood
                    holder; sits on TOP of the score chip layout because it
                    overlays the level-name strip via `pointer-events-none`. */}
                {isBoss && !locked && firstBloods[globalLevel] && (
                  <div
                    className="absolute bottom-0 left-0 right-0 px-1 py-0.5 bg-gradient-to-t from-[#ffd700]/30 to-transparent border-t border-[#ffd700]/60 font-mono-cosmic text-[7px] tracking-[0.2em] text-[#ffd700] uppercase leading-none truncate text-center pointer-events-none"
                    style={{ textShadow: "0 0 4px rgba(255,215,0,0.7)" }}
                    data-testid={`level-first-blood-${globalLevel}`}
                    title={`First Blood holder · ${firstBloods[globalLevel]}`}
                  >
                    1st · {firstBloods[globalLevel]}
                  </div>
                )}
                <div className="font-heading font-black text-sm leading-tight">
                  {localIdx + 1}
                </div>
                {score > 0 && (
                  <>
                    <div className="font-mono-cosmic text-[7px] tracking-widest text-white/50 leading-none mt-0.5">
                      {score > 999 ? `${(score / 1000).toFixed(1)}k` : score}
                    </div>
                    {/* Speedrun PB chip — only on cleared boss cards. Sits
                        just below the score so the level name still shows. */}
                    {bossCleared && bossPbLabel && (
                      <div
                        className="font-mono-cosmic text-[7px] tracking-[0.2em] text-[#ffd700] uppercase leading-none mt-0.5"
                        style={{ textShadow: "0 0 4px rgba(255,215,0,0.55)" }}
                        data-testid={`level-boss-pb-${globalLevel}`}
                        title={`Personal best clear time · ${bossPbLabel}`}
                      >
                        PB {bossPbLabel}
                      </div>
                    )}
                    {/* Space-Worm PB chip — same content shape as boss-PB
                        but tinted chrome-cyan to reinforce the worm
                        identity. Only renders on cleared every-10/20
                        cards so pilots can chase a separate speedrun
                        target on each. */}
                    {wormCleared && wormPbLabel && (
                      <div
                        className="font-mono-cosmic text-[7px] tracking-[0.2em] uppercase leading-none mt-0.5"
                        style={{
                          color: isSpaceWormGiant ? "#ff8eff" : "#c95fff",
                          textShadow: `0 0 4px ${isSpaceWormGiant ? "rgba(255,142,255,0.55)" : "rgba(201,95,255,0.45)"}`,
                        }}
                        data-testid={`level-worm-pb-${globalLevel}`}
                        title={`Space Leviathan personal best · ${wormPbLabel}`}
                      >
                        PB {wormPbLabel}
                      </div>
                    )}
                    {/* Boss rank chip — tier-coloured S/A/B/C medal that
                        appears on cleared every-10/20 Leviathan cards.
                        Pulled from `progress.boss_ranks` keyed by global
                        level. Drives the long-tail re-engagement loop:
                        pilots will replay any boss they didn't S-rank. */}
                    {wormCleared && bossRanks[String(globalLevel)] && (() => {
                      const rk = bossRanks[String(globalLevel)];
                      const rkColor = rk === "S"
                        ? "#ffd700"
                        : rk === "A"
                          ? "#7cffb0"
                          : rk === "B"
                            ? "#67e8f9"
                            : "#cbd5e1";
                      return (
                        <div
                          className="absolute top-0.5 left-0.5 font-heading text-[10px] sm:text-xs leading-none px-1 py-0.5 border bg-black/70"
                          style={{
                            color: rkColor,
                            borderColor: `${rkColor}80`,
                            textShadow: `0 0 6px ${rkColor}`,
                            boxShadow: `0 0 6px ${rkColor}40`,
                          }}
                          data-testid={`level-boss-rank-${globalLevel}`}
                          title={`Boss rank · ${rk}`}
                        >
                          {rk}
                        </div>
                      );
                    })()}
                    {/* Tiny gold callout of the level's unique name on cards
                        where the pilot has a Best — affirms ownership of the
                        clear and surfaces the procedural naming. Truncates so
                        even longer names ("Splintered Labyrinth") fit. */}
                    <div
                      className="font-mono-cosmic text-[7px] tracking-[0.15em] text-[#ffd700]/80 leading-none mt-0.5 truncate w-full text-center px-0.5"
                      data-testid={`level-name-${globalLevel}`}
                      title={getLevelName(globalLevel, sector)}
                    >
                      {getLevelName(globalLevel, sector)}
                    </div>
                  </>
                )}
                </>
                )}
              </div>
            );
            return locked ? (
              <div key={globalLevel}>{content}</div>
            ) : (
              <button
                key={globalLevel}
                onClick={() => navigate(`/play/${globalLevel}`)}
                className="cursor-pointer"
                type="button"
              >
                {content}
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
}
